import { useState } from "react";
import { Download, Smartphone, X, Share } from "lucide-react";
import usePwaInstall from "@/hooks/usePwaInstall";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * InstallAppButton — shows when the site is installable.
 * - On Chrome / Android: triggers the native install prompt.
 * - On iOS Safari: opens a friendly modal explaining the Share → Add to Home Screen steps.
 * - Hidden when already installed / running standalone.
 */
export default function InstallAppButton({ variant = "header" }) {
  const { canInstall, isStandalone, isIOS, promptInstall } = usePwaInstall();
  const [iosOpen, setIosOpen] = useState(false);

  if (isStandalone) return null;
  if (!canInstall && !isIOS) return null;

  const onClick = () => {
    if (canInstall) {
      promptInstall();
    } else if (isIOS) {
      setIosOpen(true);
    }
  };

  if (variant === "mobile") {
    return (
      <>
        <button
          type="button"
          onClick={onClick}
          data-testid="install-app-button-mobile"
          className="w-full mt-1 inline-flex items-center justify-center gap-2 bg-[#F2B441] text-[#2C2825] font-semibold py-3 rounded-full"
        >
          <Smartphone size={16} /> Install Meritz App
        </button>
        <IosHelpDialog open={iosOpen} onOpenChange={setIosOpen} />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="install-app-button"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#F2B441] text-[#2C2825] text-sm font-semibold hover:bg-[#E89B26] transition-colors"
      >
        <Download size={14} /> Install App
      </button>
      <IosHelpDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}

function IosHelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="ios-install-dialog"
        className="sm:max-w-md bg-[#FFFCF5] border-[#E8E2D6]"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 w-9 h-9 rounded-full bg-white border border-[#E8E2D6] grid place-items-center"
          aria-label="Close"
          data-testid="ios-install-close"
        >
          <X size={16} />
        </button>
        <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
          Install Meritz on iPhone
        </DialogTitle>
        <DialogDescription className="text-[#6B635A]">
          Add Meritz to your home screen for one-tap access.
        </DialogDescription>

        <ol className="mt-3 space-y-3 text-sm text-[#2C2825]">
          <li className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-[#C73E3A] text-white grid place-items-center font-bold shrink-0">
              1
            </span>
            <span>
              Tap the <strong>Share</strong> button{" "}
              <Share size={14} className="inline align-middle" /> at the bottom
              of Safari.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-[#C73E3A] text-white grid place-items-center font-bold shrink-0">
              2
            </span>
            <span>
              Scroll down and tap <strong>“Add to Home Screen”</strong>.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-[#C73E3A] text-white grid place-items-center font-bold shrink-0">
              3
            </span>
            <span>
              Tap <strong>Add</strong>. Meritz will appear on your home screen
              like a real app.
            </span>
          </li>
        </ol>

        <p className="mt-4 text-xs text-[#6B635A]">
          Works on iOS Safari only. On Android, use Chrome and tap the install
          banner.
        </p>
      </DialogContent>
    </Dialog>
  );
}
